import React, { useState } from "react";
import { Smartphone, Apple, ChevronRight, Check, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { useReferralCode } from "../hooks/useReferralCode";
import { ShareBeta } from "./ShareBeta";

const _GOOGLE_SVG = (
    <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
        <path fill="currentColor" d="M5.26 14.5l-.85 3.18-3.12.07A11 11 0 0 1 0 12c0-1.84.45-3.58 1.26-5.11l2.78.51 1.22 2.77a6.55 6.55 0 0 0 0 3.66z"/>
        <path fill="currentColor" d="M21.78 9.78c.14.74.22 1.51.22 2.22 0 .87-.09 1.71-.27 2.52a11 11 0 0 1-3.87 6.28L14.6 20.6l-.55-2.78a6.55 6.55 0 0 0 2.82-3.34h-5.21v-4.7h10.12z"/>
        <path fill="currentColor" d="M17.86 20.8A11 11 0 0 1 12 23a11 11 0 0 1-9.82-5.93l3.97-3.25a6.55 6.55 0 0 0 9.39 3.36l2.32 3.62z"/>
        <path fill="currentColor" d="M18.02 3.27l-3.97 3.25a6.55 6.55 0 0 0-9.6 3.43l-3.99-3.25A11 11 0 0 1 12 1c2.32 0 4.46.71 6.02 2.27z"/>
    </svg>
);

/**
 * Brutalist "Get early app access" CTA.
 *
 * Posts to POST /api/waitlist/signup. Idempotent — re-submitting an email returns
 * the same confirmation + queue position. Shows a lime "you're in" panel after
 * signup with the user's position in line ("#42 in line").
 */
export const AppWaitlistSection = ({ source = "home_hero" }) => {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const { code: referredBy, referrer, clear: clearReferral } = useReferralCode();

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setLoading(true);
        setError(null);
        try {
            const payload = { email: email.trim(), source };
            if (referredBy) payload.referred_by = referredBy;
            const { data } = await api.post("/waitlist/signup", payload);
            setResult(data);
            clearReferral();  // one-shot: don't re-trigger the referral on refresh
        } catch (err) {
            const msg = err?.response?.data?.detail;
            if (Array.isArray(msg)) setError(msg[0]?.msg || "Invalid email.");
            else setError(typeof msg === "string" ? msg : "Invalid email.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            data-testid="app-waitlist-section"
            className="border-y-4 border-black bg-[#CCFF00]"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
                <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
                    {/* Left — copy */}
                    <div>
                        <p className="inline-block bg-black text-[#CCFF00] border-2 border-black px-2 py-1 font-mono text-[10px] uppercase tracking-widest mb-4">
                            / Phase 2 · iOS + Android /
                        </p>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter leading-[0.9]">
                            FITS<span className="bg-black text-[#CCFF00] px-2 mx-1">ME</span><br/>
                            ON YOUR PHONE
                        </h2>
                        <p className="font-mono text-sm mt-5 leading-relaxed max-w-md">
                            Snap, scan, shop. Closed beta launching soon on TestFlight (iOS) and Google Play closed testing (Android). <strong>Invite-only.</strong> Drop your email &mdash; we&apos;ll send your tester link.
                        </p>
                        <div className="flex gap-2 mt-5 flex-wrap">
                            <span className="inline-flex items-center gap-1.5 border-2 border-black bg-black text-[#CCFF00] px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest">
                                <Apple className="w-3.5 h-3.5" /> TestFlight
                            </span>
                            <span className="inline-flex items-center gap-1.5 border-2 border-black bg-black text-[#CCFF00] px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest">
                                {_GOOGLE_SVG} Play Closed
                            </span>
                        </div>
                    </div>

                    {/* Right — form / success card */}
                    <div className="border-4 border-black bg-white shadow-brutal-lg p-6 md:p-8">
                        {referrer && !result && (
                            <div data-testid="referrer-banner" className="border-2 border-black bg-[#FF00FF] text-white px-3 py-2 font-mono text-[11px] uppercase tracking-widest mb-4 inline-flex items-center gap-2">
                                <Check className="w-3.5 h-3.5" /> {referrer.first_name} invited you
                            </div>
                        )}
                        {!result ? (
                            <form onSubmit={submit} data-testid="app-waitlist-form" className="space-y-4">
                                <div>
                                    <label htmlFor="app-waitlist-email" className="font-mono text-[10px] uppercase tracking-widest block mb-2">
                                        / Your email /
                                    </label>
                                    <input
                                        id="app-waitlist-email"
                                        data-testid="app-waitlist-email"
                                        type="email"
                                        required
                                        autoComplete="email"
                                        placeholder="you@yourcrew.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full border-2 border-black px-3 py-3 font-mono text-sm placeholder:text-black/40 focus:outline-none focus:bg-[#CCFF00]"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={loading || !email.trim()}
                                    data-testid="app-waitlist-submit"
                                    className="w-full bg-black text-[#CCFF00] border-2 border-black px-4 py-3 font-display font-black uppercase tracking-widest shadow-brutal-sm btn-press disabled:opacity-60 inline-flex items-center justify-center gap-2"
                                >
                                    <Smartphone className="w-4 h-4" />
                                    {loading ? "Adding you…" : "Get early app access"}
                                    {!loading && <ChevronRight className="w-4 h-4" />}
                                </button>
                                {error && (
                                    <p data-testid="app-waitlist-error" className="border-2 border-[#FF00FF] bg-white text-[#FF00FF] px-3 py-2 font-mono text-xs uppercase tracking-widest inline-flex items-center gap-2">
                                        <AlertTriangle className="w-3.5 h-3.5" /> {error}
                                    </p>
                                )}
                                <p className="font-mono text-[10px] text-black/60 leading-relaxed">
                                    No spam. We use your email only to send your TestFlight / Play Closed Testing invite when the beta opens.
                                </p>
                            </form>
                        ) : (
                            <div data-testid="app-waitlist-success" className="space-y-4">
                                <div className="text-center space-y-2">
                                    <div className="inline-flex items-center justify-center w-14 h-14 border-4 border-black bg-[#CCFF00] shadow-brutal-sm mx-auto">
                                        <Check className="w-7 h-7" strokeWidth={3} />
                                    </div>
                                    <h3 className="font-display text-3xl font-black uppercase tracking-tight leading-none">
                                        {result.already_signed_up ? "You&apos;re already in." : "You&apos;re on the list."}
                                    </h3>
                                    <p className="font-mono text-xs leading-relaxed">
                                        Position <span className="bg-[#FF00FF] text-white px-1.5 font-bold">#{result.position}</span> in line.
                                    </p>
                                </div>
                                {result.referral_code && (
                                    <div className="pt-3 border-t-2 border-black">
                                        <ShareBeta referralCode={result.referral_code} referralsCount={result.referrals_count || 0} tier={result.tier} />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};
